import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import {
    Form,
    FormCheckbox,
    FormTextInput
} from 'application/Shared/Components/Form';
import { ScopeTypes } from 'application/Shared/Constants';
import { Storage } from 'application/Shared/Handlers';
import Session from 'objects/Session';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MicrosoftAuth from '../../../../Login/Components/Containers/MicrosoftAuth';
import { AzureTenantSelector } from '../Microsoft/AzureTenantSelector';
import { ChooseGroups } from '../Steps/ChooseGroups';
import { Step } from './components/Step';
import { excludeGroups } from './functions';
import { microsoftAutomatedIntegrationFormSchema } from './validator';

const Hr = () => (
    <Box mx="24px" mb="24px">
        <hr />
    </Box>
);

const FormFields = ({
    accessToken,
    adminAccessGrantsByTenantId,
    filterExistingSyncedTenants = true,
    initialStep,
    selectedTenant,
    setAdminAccessGrantsByTenantId,
    setSelectedTenant,
    tenantSyncConfigId,
    tenantToEditIntegration
}) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const tenantToEditIntegrationDomains = tenantToEditIntegration?.domains;
    const tenantToEditIntegrationName = tenantToEditIntegration?.name;

    const onSelectTenant = useCallback(
        async (azureTenant) => {
            const { domains, displayName, tenantId } = azureTenant;

            // Check if an access token already exists in state for the tenant
            let adminAccessGrantedForTenant =
                adminAccessGrantsByTenantId[azureTenant.tenantId];
            // If not, check local storage.
            if (!adminAccessGrantedForTenant) {
                adminAccessGrantedForTenant =
                    await Session.getAdminAccessGrantedByTenant(
                        azureTenant.tenantId
                    );
            }
            // If there is an access token in local storage, set it in state.
            if (
                adminAccessGrantedForTenant &&
                !adminAccessGrantsByTenantId[azureTenant.tenantId]
            ) {
                setAdminAccessGrantsByTenantId({
                    ...adminAccessGrantsByTenantId,
                    [tenantId]: adminAccessGrantedForTenant === 'true'
                });
            }

            setSelectedTenant(azureTenant);

            const combinedDomains = tenantToEditIntegrationDomains
                ? domains.concat(tenantToEditIntegrationDomains)
                : domains;

            // Remove duplicates
            const uniqueDomains = Array.from(new Set(combinedDomains));
            setValue('domains', uniqueDomains.join());
            if (tenantToEditIntegrationDomains) {
                setValue('updateDomains', true);
            }
            setValue('name', tenantToEditIntegrationName || displayName);
            setValue('azureTenantId', tenantId);
        },
        [
            adminAccessGrantsByTenantId,
            setAdminAccessGrantsByTenantId,
            setSelectedTenant,
            setValue,
            tenantToEditIntegrationDomains,
            tenantToEditIntegrationName
        ]
    );

    const selectedTenantId = selectedTenant?.tenantId;
    const adminAccessGrantedForTenant =
        adminAccessGrantsByTenantId[selectedTenantId];
    // Anytime the selectedTenant or adminAccessGrantedForTenant values change, update the checkbox value.
    useEffect(() => {
        setValue('useAsmAppCredentials', !!adminAccessGrantedForTenant);
    }, [selectedTenantId, adminAccessGrantedForTenant, setValue]);

    return (
        <>
            <Step
                number={initialStep}
                details={t('Clients.addModal.steps.3.automated')}
            />
            <Box mx="24px" mb="24px">
                <AzureTenantSelector
                    filterExistingSyncedTenants={filterExistingSyncedTenants}
                    accessToken={accessToken}
                    onSelectTenant={onSelectTenant}
                />

                <FormTextInput
                    name="azureTenantId"
                    label={t('Provider.MicrosoftConfiguration.tenant_id')}
                    required
                    inputProps={{ readOnly: true }}
                />
                <FormTextInput
                    inputProps={{
                        readOnly:
                            !!tenantSyncConfigId || tenantToEditIntegrationName
                    }}
                    label={t('Common.client_name')}
                    name="name"
                    placeholder={t('Common.brand_name')}
                    required
                />
                <FormTextInput
                    name="domains"
                    inputProps={{ readOnly: true }}
                    label={t('Clients.domain(s)')}
                    placeholder="saas.com,saas.onmicrosoft.com"
                    required
                    tooltipText={t('Tooltips.domains_comma_separated')}
                />
            </Box>

            {selectedTenant && (
                <>
                    <Hr />

                    <Step
                        number={initialStep + 1}
                        details={t('Clients.addModal.steps.4.automated')}
                    />
                    <Box mx="24px" mb="24px">
                        <Box mb="24px">
                            <MicrosoftAuth
                                azureTenantId={selectedTenant.tenantId}
                                scopeType={ScopeTypes.AppAuth}
                                buttonTextKey="Clients.addModal.MicrosoftAuth.appAuth"
                                disabled={adminAccessGrantedForTenant}
                            />
                        </Box>
                        <FormCheckbox
                            name="useAsmAppCredentials"
                            required
                            readOnly
                            label={
                                <Box
                                    as="span"
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'Clients.addModal.MicrosoftAuth.accessGrantedCheckbox',
                                            {
                                                tenantName:
                                                    selectedTenant.displayName
                                            }
                                        )
                                    }}
                                />
                            }
                        />
                    </Box>
                </>
            )}
        </>
    );
};

export const MicrosoftAutomatedIntegrationForm = ({
    clientId,
    children,
    filterExistingSyncedTenants = true,
    initialStep = 3,
    onboarding,
    onSubmit,
    tenantSyncConfigId,
    tenantToEditIntegration,
    useIntegrationWizard
}) => {
    const { t } = useTranslation();

    const [accessToken, setAccessToken] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [adminAccessGrantsByTenantId, setAdminAccessGrantsByTenantId] =
        useState({});
    const [groups, setGroups] = useState({});

    const selectedTenantId = selectedTenant?.tenantId;
    const providerRoute = 'azure';
    const handleStorage = useCallback(
        async (message) => {
            const { key } = message;
            if (key === 'accessToken') {
                // using the helper here so that the token is extracted in a consistent manor and we don't have to duplicate the parsing logic.
                const token = await Storage.getWithExpiry(key);
                setAccessToken(token);
            }

            if (key === `adminAccessGranted-${selectedTenantId}`) {
                const accessGranted = (await Storage.get(key)) === 'true';
                setAdminAccessGrantsByTenantId({
                    ...adminAccessGrantsByTenantId,
                    [selectedTenantId]: accessGranted
                });
            }
        },
        [adminAccessGrantsByTenantId, selectedTenantId]
    );

    useEffect(() => {
        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [handleStorage]);

    useEffect(() => {
        const fetchAccessToken = async () => {
            const token = await Session.getAccessToken();
            if (token) {
                setAccessToken(token);
            }
        };

        fetchAccessToken();
    }, []);

    const onDone = async (values) => {
        const providerInfo = {
            identityTenantId: selectedTenantId,
            platformTenantId: clientId,
            useAsmAppCredentials: true
        };
        const success = await excludeGroups({
            clientId,
            groups,
            onboarding,
            providerInfo,
            providerRoute,
            t,
            useIntegrationWizard
        });
        if (success) {
            await onSubmit(values);
        }
    };

    return (
        <Box display="flex" flexDirection="column">
            <Box mx="24px" mb="24px">
                <MicrosoftAuth
                    scopeType={ScopeTypes.Impersonation}
                    buttonTextKey="Clients.addModal.MicrosoftAuth.tenants"
                />
            </Box>
            {accessToken && <Hr />}
            <Form
                defaultValues={{
                    azureTenantId: '',
                    domains: '',
                    enableAzure: true,
                    providerType: 'Microsoft',
                    updateDomains: null,
                    useAsmAppCredentials: false
                }}
                validator={yupResolver(
                    microsoftAutomatedIntegrationFormSchema({
                        t,
                        tenantSyncConfigId
                    })
                )}
                onSubmit={onDone}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                <Box display="flex" flexDirection="column" height="99%">
                    {accessToken && (
                        <FormFields
                            accessToken={accessToken}
                            adminAccessGrantsByTenantId={
                                adminAccessGrantsByTenantId
                            }
                            filterExistingSyncedTenants={
                                filterExistingSyncedTenants
                            }
                            initialStep={initialStep}
                            selectedTenant={selectedTenant}
                            setAdminAccessGrantsByTenantId={
                                setAdminAccessGrantsByTenantId
                            }
                            setSelectedTenant={setSelectedTenant}
                            tenantSyncConfigId={tenantSyncConfigId}
                            tenantToEditIntegration={tenantToEditIntegration}
                        />
                    )}
                    {adminAccessGrantsByTenantId[selectedTenantId] &&
                        !useIntegrationWizard &&
                        !onboarding && (
                            <>
                                <Hr />
                                <ChooseGroups
                                    groups={groups}
                                    integrationType="microsoftEntraIDAutomated"
                                    identityTenantId={selectedTenantId}
                                    linkedTenantId={clientId}
                                    providerRoute="azure"
                                    setGroups={setGroups}
                                    useAsmAppCredentials={
                                        !!adminAccessGrantsByTenantId
                                    }
                                />
                            </>
                        )}
                    {children}
                </Box>
            </Form>
        </Box>
    );
};
